/**
 * Statuses hidden when "Show completed" is off.
 * In-progress states stay visible: deploymentRequested, deploying, running,
 * terminationRequested. CaaS uses `pending` for the same state VMaaS calls
 * deployment requested.
 */
export const COMPLETED_CLUSTER_STATUSES = [
  "completed",
  "failed",
  "destroyed",
  "terminated",
  "archived",
  "unpaid",
  "cancelled",
  "canceled"
] as const;

export const normalizeClusterStatusKey = (status: string) =>
  status.toLowerCase().replace(/[\s_-]+/g, "");

const COMPLETED_CLUSTER_STATUS_KEYS = new Set(
  COMPLETED_CLUSTER_STATUSES.map((status) => normalizeClusterStatusKey(status))
);

const ZERO_COMPUTE_DURATION = /^0\s*(hrs?|hours?)\s+0\s*(mins?|minutes?)$/i;

export const isZeroComputeDuration = (value?: string) =>
  typeof value === "string" && ZERO_COMPUTE_DURATION.test(value.trim());

export const isCompletedClusterStatus = (status: string) =>
  COMPLETED_CLUSTER_STATUS_KEYS.has(normalizeClusterStatusKey(status));

export const isDeploymentRequestedStatus = (status?: string) => {
  if (!status) {
    return false;
  }
  const key = normalizeClusterStatusKey(status);
  return key === "deploymentrequested" || key === "pending";
};

export const isClusterAwaitingStart = (status?: string) => {
  if (!status) {
    return false;
  }
  const key = normalizeClusterStatusKey(status);
  return key === "deploying" || isDeploymentRequestedStatus(status);
};

type ClusterStatusSource = {
  status?: string;
  completed_percent?: number;
  percent_completed?: number;
  percentCompleted?: number;
  served?: string;
  compute_hours_served?: string;
  compute_minutes_remaining?: number;
  remaining?: string;
};

/**
 * CaaS uses `pending` for deployment requested. Trust explicit terminal states:
 * a cluster can complete before serving any compute, including a canceled deploy.
 */
export const shouldMapToDeploymentRequested = (result: ClusterStatusSource) =>
  normalizeClusterStatusKey(String(result.status || "")) === "pending";
